import { Directive, HostListener, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DwDataSort,
  DwEventService,
  DwGridConfig,
  DwItemAttribute,
  DwLookupInfo,
  DwMetaDataFilterTileState,
  DwMetaDataRoutingService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryFilterGroup,
  DwQueryParam,
  DwRoutingService,
  DwSecurityUserService
} from '@devwareapps/devware-cap';
import { combineLatest, of } from 'rxjs';

import { AppMetaDataItemNames, AppMetaDataLookups } from '../../../../meta-data/app-meta-data.service';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';

interface AssignmentGridViewConfig {
  key: string;
  display: string;
}

interface AssignmentGridConfigWithViews {
  views?: AssignmentGridViewConfig[];
  selectedView?: AssignmentGridViewConfig | null;
}

export interface AssignmentGridFilterAttributes {
  courseId?: string | DwItemAttribute;
  instructorId?: string | DwItemAttribute;
  groupId?: string | DwItemAttribute;
  statusId?: string | DwItemAttribute;
}

@Directive()
export abstract class AssignmentGridRendererBaseComponent<TItem>
  extends DwAviatorGridRendererBaseComponent<TItem>
  implements OnInit {

  coursesLookupId = AppMetaDataLookups.CourseBySchool;
  instructorsLookupId = AppMetaDataLookups.InstructorsBySchool;
  groupsLookupId = AppMetaDataLookups.FlightSchoolGroupBySchool;
  statusLookupId = AppMetaDataLookups.AssignmentStatusAllItems;

  public sortColumn: string = 'DueDateTime';
  public sortDirection: 'asc' | 'desc' = 'asc';

  public showFilterPanel: boolean = false;
  public searchText: string = '';
  public canAddAssignments: boolean = false;
  public gridViewOptions: { key: string; display: string }[] = [];
  public selectedViewKey: string | null = null;

  public customFilters: {
    courseId: string | null;
    instructorId: string | null;
    groupId: string | null;
    statusId: string | null;
  } = {
      courseId: null,
      instructorId: null,
      groupId: null,
      statusId: null
    };

  public courseOptions: DwLookupInfo[] = [];
  public instructorOptions: DwLookupInfo[] = [];
  public groupOptions: DwLookupInfo[] = [];
  public filterAttributes: AssignmentGridFilterAttributes = {};

  isMobile = false;
  lookupParams: (number | null | undefined)[] = [];

  noRowsConfig: {
    title: string;
    message: string;
    showAction?: boolean;
    actionText?: string;
    iconClass?: string;
    action?: () => void;
  } = {
      title: 'No assignments found',
      message: 'Try adjusting your search or filter criteria.',
      showAction: false,
      iconClass: 'fa fa-tasks'
    };

  constructor(
    protected dwRoutingService: DwRoutingService,
    protected adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) protected dwMetaDataService: DwMetaDataService,
    protected dwEventService: DwEventService,
    protected dwMetaDataRoutingService: DwMetaDataRoutingService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected dwSecurityUserService: DwSecurityUserService
  ) {
    super(dwRoutingService, adminManagerService, dwMetaDataService, dwEventService);
  }

  ngOnInit(): void {
    this.filterAttributes = this.getFilterAttributes();
    this.checkMobile();
    this.subscriptions.push(this.dwSecurityUserService.securityContextChanged().subscribe(() => {
      this.syncAssignmentPermissions();
    }));
    this.setupLookups();
    this.configureTextSearch(this.getTextSearchAttributes(), 500);
  }

  setupRenderer(): void {
    this.syncGridViews();
    this.applyCurrentSort();
  }

  protected onGridConfigUpdated(): void {
    this.syncGridViews();
  }

  protected filterTileStateChange(filterTileState: DwMetaDataFilterTileState): void {
    const showCreateAction = filterTileState?.metricValue === 0 && this.canAddAssignments;

    if (filterTileState?.metricValue === 0) {
      this.noRowsConfig = {
        title: 'No assignments yet',
        message: showCreateAction ? 'Create your first assignment to get started.' : 'Assignments will appear here once they are created.',
        showAction: showCreateAction,
        actionText: 'Add Assignment',
        iconClass: filterTileState.filterTileConfig?.tileIconClass || 'fa fa-tasks',
        action: () => {
          this.addAssignment();
        }
      };

      return;
    }

    this.noRowsConfig = {
      title: 'No assignments found',
      message: 'Try adjusting your search or filter criteria.',
      showAction: false,
      iconClass: filterTileState?.filterTileConfig?.tileIconClass || 'fa fa-tasks'
    };
  }

  noRowsAction(): void {
    if (this.noRowsConfig?.showAction && this.noRowsConfig?.action) {
      this.noRowsConfig.action();
    }
  }

  setupLookups(): void {
    const aviatorUser = this.adminManagerService.getAviatorUser();

    this.syncAssignmentPermissions();

    this.lookupParams = [aviatorUser.FlightSchoolId];
    this.loadLookupValues(aviatorUser.FlightSchoolId);
  }

  protected syncAssignmentPermissions(): void {
    this.canAddAssignments = this.dwSecurityUserService.hasPermission(AppPermissions.assignmentsCanCreate)
      || this.dwSecurityUserService.hasPermission(AppPermissions.assignmentsCanCreateForSchool);
  }

  loadLookupValues(flightSchoolId?: number | null): void {
    const schoolParams: DwQueryParam[] = [{ ParamName: 'FlightSchoolId', ParamValue: flightSchoolId?.toString() || '' }];

    const courses$ = this.dwMetaDataService.getLookup(this.coursesLookupId, schoolParams) || of([]);
    const instructors$ = this.dwMetaDataService.getLookup(this.instructorsLookupId, schoolParams) || of([]);
    const groups$ = this.dwMetaDataService.getLookup(this.groupsLookupId, schoolParams) || of([]);

    combineLatest([courses$, instructors$, groups$]).subscribe(([courses, instructors, groups]) => {
      this.courseOptions = courses || [];
      this.instructorOptions = instructors || [];
      this.groupOptions = groups || [];
    });
  }

  @HostListener('window:resize')
  public windowResize(): void {
    this.checkMobile();
  }

  public get showCourseFilter(): boolean {
    return !!this.filterAttributes.courseId;
  }

  public get showInstructorFilter(): boolean {
    return !!this.filterAttributes.instructorId;
  }

  public get showGroupFilter(): boolean {
    return !!this.filterAttributes.groupId;
  }

  public get showStatusFilter(): boolean {
    return !!this.filterAttributes.statusId;
  }

  public toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
  }

  public viewChanged(viewKey: string): void {
    if (!viewKey || viewKey === this.selectedViewKey) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { view: viewKey },
      queryParamsHandling: 'merge'
    });
  }

  public clearCustomFilters(): void {
    this.customFilters.courseId = null;
    this.customFilters.instructorId = null;
    this.customFilters.groupId = null;
    this.customFilters.statusId = null;
    this.hasFilters = false;
    this.customFiltersChanged();
  }

  public searchTextChanged(): void {
    this.applyTextFilter(this.searchText);
  }

  public customFiltersChanged(): void {
    const filterGroup = new DwQueryFilterGroup();
    const attrs = this.filterAttributes;

    this.addOptionalFilter(filterGroup, this.customFilters.courseId, attrs.courseId);
    this.addOptionalFilter(filterGroup, this.customFilters.instructorId, attrs.instructorId);
    this.addOptionalFilter(filterGroup, this.customFilters.groupId, attrs.groupId);
    this.addOptionalFilter(filterGroup, this.customFilters.statusId, attrs.statusId);

    this.applyCustomFilters(filterGroup);
  }

  public toggleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = this.getDefaultSortDirection(column);
    }

    this.applyCurrentSort();
  }

  public addAssignment(): void {
    this.dwMetaDataRoutingService.routeToAdd(this.activatedRoute, AppMetaDataItemNames.Assignment);
  }

  public refresh(): void {
    this.refreshDataSource();
  }

  protected resolveInitialPageSize(): number {
    return 50;
  }

  protected getEffectiveSorts(): DwDataSort[] {
    if (!this.sortColumn) {
      return [];
    }

    return [
      {
        fieldName: this.sortColumn,
        ascending: this.sortDirection === 'asc'
      } as DwDataSort
    ];
  }

  public onScroll(): void {
    this.nextPage();
  }

  protected abstract getTextSearchAttributes(): (string | DwItemAttribute)[];
  protected abstract getFilterAttributes(): AssignmentGridFilterAttributes;

  protected getDefaultSortDirection(column: string): 'asc' | 'desc' {
    return column === 'DueDateTime' ? 'asc' : 'desc';
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private applyCurrentSort(): void {
    this.setSorts(this.getEffectiveSorts());
  }

  private addOptionalFilter(filterGroup: DwQueryFilterGroup, value: string | null, attr?: string | DwItemAttribute): void {
    if (!attr || !value) {
      return;
    }

    this.addFilter(filterGroup, value, attr);
  }

  private syncGridViews(): void {
    const gridConfigWithViews = this.gridConfig as DwGridConfig & AssignmentGridConfigWithViews;

    this.gridViewOptions = (gridConfigWithViews?.views || []).map(view => ({
      key: view.key,
      display: view.display
    }));
    this.selectedViewKey = gridConfigWithViews?.selectedView?.key || null;
  }
}
